import { useState, useEffect, useCallback } from "react";
import { NavLink } from "react-router-dom";
import {
  LayoutDashboard, CalendarClock, CalendarDays, Users, ReceiptIndianRupee,
  FileText, Award, FlaskConical, ClipboardList, ChartColumn, BuildingComplex,
  UserCog, ScrollText, Settings as SettingsIcon, Pill, Tag, Flag, Bug,
  ChevronDown, Pin, PinOff, FileOutput, DatabaseBackup, ClipboardPen,
  type LucideIcon,
} from "lucide-react";
import { Has } from "../Has";
import { usePublicAppSettings, useNavPins } from "@/lib/queries";
import { useReplaceNavPins } from "@/lib/mutations";
import { useAuth } from "@/context/AuthContext";
import { useQueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/utils";

const FALLBACK_CLINIC_NAME = "PS Neuro Centre";
const PIN_STORAGE_PREFIX = "emr:pinnedNav:";
const PIN_MIGRATED_PREFIX = "emr:pinnedNavMigrated:";

interface NavItem { to: string; label: string; icon: LucideIcon; perm?: string; adminOnly?: boolean; }

const core: NavItem[] = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/appointments", label: "Appointments", icon: CalendarClock, perm: "appointments.view" },
  { to: "/pending-appointments", label: "Pending Appointments", icon: CalendarDays, perm: "appointments.view" },
  { to: "/patients", label: "Patients", icon: Users, perm: "patients.view" },
  { to: "/billing", label: "Billing", icon: ReceiptIndianRupee, perm: "billing.view" },
  { to: "/discharge-summaries", label: "Discharge Summaries", icon: FileOutput, perm: "dischargesummaries.view" },
  { to: "/prescription-templates", label: "Prescription Templates", icon: ClipboardPen, perm: "prescriptions.view" },
];

const documents: NavItem[] = [
  { to: "/referrals", label: "Referrals", icon: FileText, perm: "referrals.view" },
  { to: "/certificates", label: "Certificates", icon: Award, perm: "certificates.view" },
  { to: "/investigations", label: "Investigations", icon: FlaskConical, perm: "investigations.view" },
  { to: "/patient-info", label: "Patient Info", icon: ClipboardList, perm: "patientinfo.view" },
  { to: "/reports", label: "Reports", icon: ChartColumn, perm: "reports.view" },
  { to: "/referring-doctors", label: "Referring Doctors", icon: BuildingComplex, perm: "referringdoctors.view" },
];

const admin: NavItem[] = [
  { to: "/admin/users", label: "Users & Permissions", icon: UserCog, perm: "users.view" },
  { to: "/admin/audit-log", label: "Audit Log", icon: ScrollText, perm: "auditlog.view" },
  { to: "/admin/error-log", label: "Error Log", icon: Bug, perm: "errorlog.view" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, perm: "settings.view" },
  { to: "/admin/drugs", label: "Drug Master", icon: Pill, perm: "drugs.view" },
  { to: "/admin/services", label: "Service Catalog", icon: Tag, perm: "services.view" },
  { to: "/admin/flags", label: "Flags", icon: Flag, perm: "flags.view" },
  // Backups (2026-10-18): admin-ONLY (no perm key — backups are admin-only by
  // design, same hard-gate as the slot-config apply wizard). nav_pins
  // compatibility: a non-admin who somehow pinned /backups leaves an orphaned
  // pin row — harmless (SCHEMA.md: orphaned pins are never rendered).
  { to: "/backups", label: "Backups", icon: DatabaseBackup, adminOnly: true },
];

interface Section { key: string; title: string; items: NavItem[]; }

const SECTIONS: Section[] = [
  { key: "core", title: "Core", items: core },
  { key: "documents", title: "Documents", items: documents },
  { key: "admin", title: "Admin", items: admin },
];

/**
 * Per-user pinned nav routes, persisted server-side in the `nav_pins` table
 * so pins survive cache clears and are restored on re-login.
 *
 * One-time localStorage→DB migration: if the user had pins in localStorage
 * (from the old implementation) and the DB has no pins yet, we upload the
 * localStorage set to the server and mark the migration done so we never
 * re-upload. This bridges existing users when DB-backed pinning ships.
 */
function usePinnedNav(userId: string | undefined) {
  const qc = useQueryClient();
  const { mustChangePassword } = useAuth();
  const { data: serverPinned, isLoading } = useNavPins();
  const replacePins = useReplaceNavPins();

  // One-time migration from localStorage to the DB. Runs only once per user
  // per browser, guarded by a separate "migrated" flag so a re-login after
  // cache clear (which wipes the migrated flag) is harmless — the DB already
  // holds the pins, so the migration uploads nothing.
  const migratedKey = userId ? `${PIN_MIGRATED_PREFIX}${userId}` : null;
  const legacyKey = userId ? `${PIN_STORAGE_PREFIX}${userId}` : null;

  useEffect(() => {
    if (!userId || !migratedKey || !legacyKey || isLoading) return;

    // Flagged users (mustChangePassword) are blocked from mutating routes
    // server-side (403 password_change_required, ce556c9's gate). The
    // migration PUT would just bounce off the gate and surface a stray
    // "Failed to save pinned items" toast under the non-dismissible
    // force-change dialog. Skip silently — the migration re-runs naturally
    // after they comply (page reload clears the flag).
    if (mustChangePassword) return;

    try {
      const alreadyMigrated = localStorage.getItem(migratedKey);
      if (alreadyMigrated) return; // migration already happened

      const raw = localStorage.getItem(legacyKey);
      const localRoutes: string[] = raw ? (JSON.parse(raw) as string[]) : [];

      // Only upload if the user actually had localStorage pins AND the server
      // currently has none (so we never clobber server-side pins set elsewhere).
      if (localRoutes.length > 0 && (!serverPinned || serverPinned.size === 0)) {
        replacePins.mutate(localRoutes, {
          onSuccess: () => {
            localStorage.setItem(migratedKey, "1");
          },
        });
      } else {
        // No local pins to migrate, or server already has pins — just mark done.
        localStorage.setItem(migratedKey, "1");
      }
    } catch {
      // localStorage access failure — non-fatal; server pins are authoritative.
    }
  }, [userId, migratedKey, legacyKey, isLoading, serverPinned, replacePins, mustChangePassword]);

  // Pinned set comes from the server query. While loading, use an empty set so
  // the UI renders with no pins briefly rather than throwing.
  const pinned = serverPinned ?? new Set<string>();

  const togglePin = useCallback(
    (to: string) => {
      const next = new Set(pinned);
      if (next.has(to)) next.delete(to);
      else next.add(to);
      const routes = [...next];

      // Optimistic update: set the cache immediately so the UI reflects the
      // toggle without waiting for the server round-trip.
      qc.setQueryData<Set<string>>(["nav-pins"], next);

      replacePins.mutate(routes, {
        onError: () => {
          // Roll back to the server-known state on failure.
          qc.setQueryData<Set<string>>(["nav-pins"], pinned);
        },
      });
    },
    [pinned, qc, replacePins],
  );

  return { pinned, togglePin };
}

function NavRow({
  it,
  collapsed,
  pinned,
  onTogglePin,
}: {
  it: NavItem;
  collapsed: boolean;
  pinned: boolean;
  onTogglePin: (to: string) => void;
}) {
  const { isAdmin } = useAuth();
  const Icon = it.icon;
  const inner = (
    <>
      <Icon size={18} className="shrink-0" />
      {!collapsed && <span className="truncate flex-1">{it.label}</span>}
      {!collapsed && (
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onTogglePin(it.to); }}
          title={pinned ? "Unpin from top" : "Pin to top"}
          className={cn(
            "shrink-0 rounded p-1 text-cream-100/50 transition-colors hover:bg-maroon-700 hover:text-amber-300",
            pinned && "text-amber-300 hover:text-amber-200",
          )}
        >
          {pinned ? <PinOff size={13} /> : <Pin size={13} />}
        </button>
      )}
    </>
  );
  const cls = ({ isActive }: { isActive: boolean }) =>
    cn("flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
      isActive ? "bg-maroon-600 text-cream-50" : "text-cream-100/80 hover:bg-maroon-700/60 hover:text-cream-50");

  // adminOnly items render ONLY for admins (no perm key exists for backups —
  // the API hard-gates them via requireAdmin()). Renders nothing for
  // non-admins, so pinned orphan rows are never shown.
  if (it.adminOnly && !isAdmin) return null;

  return it.perm ? (
    <Has key={it.to} perm={it.perm}>
      <NavLink to={it.to} className={cls} end={it.to === "/dashboard"} title={collapsed ? it.label : undefined}>{inner}</NavLink>
    </Has>
  ) : (
    <NavLink key={it.to} to={it.to} className={cls} end={it.to === "/dashboard"} title={collapsed ? it.label : undefined}>{inner}</NavLink>
  );
}

function AccordionSection({
  section,
  open,
  onToggle,
  collapsed,
  pinned,
  onTogglePin,
}: {
  section: Section;
  open: boolean;
  onToggle: () => void;
  collapsed: boolean;
  pinned: Set<string>;
  onTogglePin: (to: string) => void;
}) {
  // When collapsed, render only pinned items as a flat icon stack — no
  // accordion. Unpinned items are hidden to keep the minimized rail focused
  // on the user's pinned tabs (mirrors the expanded closed-section behavior).
  if (collapsed) {
    const pinnedRows = section.items.filter((it) => pinned.has(it.to));
    if (pinnedRows.length === 0) return null;
    return (
      <div className="px-2 py-1 space-y-1">
        {pinnedRows.map((it) => (
          <NavRow key={it.to} it={it} collapsed pinned onTogglePin={onTogglePin} />
        ))}
      </div>
    );
  }
  // Pinned items always stay visible under the section header; the rest only
  // appear when the accordion is open. Order pinned first so they anchor top.
  const pinnedRows = section.items.filter((it) => pinned.has(it.to));
  const otherRows = section.items.filter((it) => !pinned.has(it.to));
  const renderRow = (it: NavItem) => (
    <NavRow key={it.to} it={it} collapsed={false} pinned={pinned.has(it.to)} onTogglePin={onTogglePin} />
  );

  return (
    <div className="px-3 py-1">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-cream-100/70 transition-colors hover:bg-maroon-700/40 hover:text-cream-50"
      >
        <span>{section.title}</span>
        <ChevronDown size={14} className={cn("shrink-0 transition-transform duration-200", open && "rotate-180")} />
      </button>
      <nav className="mt-1 space-y-1">
        {pinnedRows.map(renderRow)}
        {open && otherRows.map(renderRow)}
      </nav>
    </div>
  );
}

export function Sidebar({ collapsed }: { collapsed: boolean }) {
  const { data: settings } = usePublicAppSettings();
  const { user } = useAuth();
  const clinicName = settings?.clinicName ?? FALLBACK_CLINIC_NAME;
  const userId = user?.id;

  const { pinned, togglePin } = usePinnedNav(userId);

  // Accordion open state. Per spec: sections start CLOSED on load so only
  // pinned items are visible under each header; the user opens sections
  // manually.
  const [openSections, setOpenSections] = useState<Set<string>>(new Set());

  const toggleSection = useCallback((key: string) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  return (
    <aside className={cn("flex shrink-0 flex-col bg-maroon-800 text-cream-100 no-print transition-all", collapsed ? "w-16" : "w-60")}>
      <div className="flex h-14 items-center gap-2 px-3 border-b border-maroon-700/50">
        <img
          src="/assets/logo.png"
          alt={`${clinicName} logo`}
          className="h-9 w-9 shrink-0 rounded-full object-contain bg-cream-50 dark:bg-dark-900 p-0.5"
          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
        />
        {!collapsed && <span className="font-serif text-lg font-semibold text-cream-50 truncate">{clinicName}</span>}
      </div>
      <div className="flex-1 overflow-y-auto py-2">
        {SECTIONS.map((s, i) => (
          <div key={s.key}>
            <AccordionSection
              section={s}
              open={openSections.has(s.key)}
              onToggle={() => toggleSection(s.key)}
              collapsed={collapsed}
              pinned={pinned}
              onTogglePin={togglePin}
            />
            {i < SECTIONS.length - 1 && <div className="my-1 h-px bg-maroon-700/40" />}
          </div>
        ))}
      </div>
      <div className="px-4 py-3 text-[10px] text-cream-100/70 border-t border-maroon-700/50">{clinicName} v0.1</div>
    </aside>
  );
}
